import React, { useState, useEffect, useCallback } from "react";
import { ShortlistType as ShortlistTypeEnums, type Shortlist as ShortlistType, type ShortlistListProps } from "./types";
import { useAppSelector } from "../../../../../util/store-types";
import { DiscoSearchServiceClient } from "clients/disco-search-service-client";
import ModalDrawer from "../shared/modal-drawer/modal-drawer";
import LoadingSpinner from "../new_taxonomy/loading-spinner";
import ShortlistImages from "./shortlist-images";
import Shortlist from "./shortlist";
import ShortlistForm from "./shortlist-form";
import HeapHelpers from "helpers/heap-helpers";

const ShortlistList = (props: ShortlistListProps) => {
  const {
    addStyleToShortlist,
    fetchShortlists,
    onClose,
    productId,
    removeProductRating,
    removeStyleFromShortlist,
    isMobileViewport,
    product,
    isOpen,
  } = props;

  const userId = useAppSelector(state => state.userData?.userProfile?.id);
  const [shortlistSummary, setShortlistSummary] = useState<ShortlistType[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const fetchShortlistSummary = useCallback(() => {
    if (userId) {
      setIsLoading(true);
      DiscoSearchServiceClient.getInstance()
        .userShortlistSummary(userId)
        .then(summary => {
          setShortlistSummary(summary || []);
        })
        .catch(error => {
          console.error("Failed to fetch shortlist summary", error);
        })
        .finally(() => setIsLoading(false));
      HeapHelpers.fireHeapEvent("view_shortlist_summary", {});
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen) {
      fetchShortlistSummary();
    }
  }, [isOpen, fetchShortlistSummary]);

  const onClickFavoriteShortlist = () => {
    removeProductRating(productId);
    onClose();
  };

  const showCreateForm = () => {
    setShowForm(true);
  };

  const hideShortlistCreateForm = (event?: React.MouseEvent) => {
    event?.preventDefault();
    setShowForm(false);
  };

  const handleCreateSuccess = () => {
    setShowForm(false);
    fetchShortlistSummary();
    onClose();
  };

  const handleClose = () => {
    HeapHelpers.fireHeapEvent("close_shortlist_summary", {});
    onClose();
  };

  const renderSingleShortlist = (shortlist: ShortlistType, i: number) => {
    const styleNames =
      shortlist?.previewImages
        ?.map(image => {
          const parts = image?.split("/");
          const lastPart = parts?.[parts.length - 1];
          return lastPart?.replace(/\.[^/.]+$/, "");
        })
        .filter((name): name is string => typeof name === "string") || [];

    return (
      <li key={shortlist.shortlistId || i} className="border-t border-lighterGray first:border-t-0 last:border-b">
        <div className="flex w-full items-center px-4 py-4">
          <div>
            <ShortlistImages images={shortlist?.previewImages} />
          </div>

          <div className="flex-grow">
            <Shortlist
              addStyleToShortlist={addStyleToShortlist}
              onClickFavoriteShortlist={onClickFavoriteShortlist}
              productId={productId}
              removeStyleFromShortlist={removeStyleFromShortlist}
              shortlist={shortlist}
              styleNames={styleNames}
            />
          </div>
        </div>
      </li>
    );
  };

  const renderShortListsContent = () => {
    const paddingTopClass = isMobileViewport ? "pt-[17px]" : "pt-[13px]";
    const footerPaddingClass = isMobileViewport ? "pb-[88px]" : "md:pb-[60px]";

    return (
      <div className={`flex flex-col justify-between absolute inset-0`}>
        <div className="flex-shrink-0">
          <div className={`w-full flex items-center justify-center px-6 pb-2 ${paddingTopClass}`}>
            <h4 className="pb-2">Organize your favorites</h4>
          </div>
          <hr className="w-full border-lighterGray" />
        </div>

        <div className={`flex-grow overflow-y-auto ${footerPaddingClass}`}>
          {isLoading ? (
            <div className="mt-4 flex justify-center items-center h-full">
              <LoadingSpinner />
            </div>
          ) : (
            <ul className="text-left list-none">
              {shortlistSummary.map(
                (shortlist, i) =>
                  shortlist?.type !== ShortlistTypeEnums.HEARTS_ON_SALE &&
                  shortlist?.type !== ShortlistTypeEnums.DOWN_VOTES &&
                  renderSingleShortlist(shortlist, i)
              )}
            </ul>
          )}
        </div>

        <div
          className={`absolute bottom-0 left-0 right-0 bg-white p-6 flex gap-4 items-center ${
            !isLoading ? "border-t border-lighterGray" : ""
          } z-20`}>
          {!isLoading && (
            <>
              <button
                onClick={showCreateForm}
                type="button"
                className="flex-1 px-6 py-3 border border-wine rounded-full text-wine font-medium text-base hover:bg-gray-100 transition-colors duration-200">
                Create List
              </button>
              <button
                data-style-name={productId}
                onClick={handleClose}
                type="submit"
                className="flex-1 px-6 py-3 bg-wine text-white rounded-full font-medium text-base hover:bg-gray-800 transition-colors duration-200">
                Done
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const contentStyles = isMobileViewport
    ? {
        height: "100%",
        borderRadius: 0,
      }
    : {};

  return (
    <ModalDrawer
      desktopWidth="34%"
      peekHeight="100svh"
      isOpen={isOpen}
      handleClose={handleClose}
      contentStyles={contentStyles}
      hideDragHandle={isMobileViewport}
      isShortlist={true}>
      {showForm ? (
        <ShortlistForm
          fetchShortlists={fetchShortlists}
          hideShortlistCreateForm={hideShortlistCreateForm}
          product={product}
          productId={productId}
          toggleForm={handleCreateSuccess}
          isMobileViewport={isMobileViewport}
        />
      ) : (
        renderShortListsContent()
      )}
    </ModalDrawer>
  );
};

export default ShortlistList;
